import { useState } from "react";
// import { sendRequest } from "../utils/api";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import User from "../../models/User";
import Permission from "../../models/Permission";

interface RBACModalProps {
    staff: User;
    closeModal: () => void;
    onSave?: (
        staffId: string,
        payload: { name: string; type: string; value: boolean }[]
    ) => void;
}

const permissionsList: Permission[] = [
    { name: "staff", displayName: "Staff", type: "view", description: "View and manage staff members" },
    { name: "Logs", displayName: "Logs", type: "view", description: "View and manage logs" },
    { name: "expenditures", displayName: "Expenditures", type: "view", description: "View and manage expenditures records" },
    { name: "maintenances", displayName: "Maintenances", type: "view", description: "View and manage maintenances records" },
    {
        name: "settings", displayName: "Settings", type: "view",
        description: "View and manage system configuration."
    },
    {
        name: "expense_approve_reject",
        displayName: "Approve or Reject Expense Requests",
        type: "action",
        description: "Allow approving or rejecting staff expense requests",
    },
    {
        name: "loans_approve_reject",
        displayName: "Approve or Reject Loans",
        type: "action",
        description: "Allow approving or rejecting staff loan requests",
    },
    {
        name: "leaves_approve_reject",
        displayName: "Approve or Reject Leave Requests",
        type: "action",
        description: "Allow approving or rejecting staff leave requests",
    },

];

type PermissionsState = Record<string, boolean>;

function RBACModal({ staff, closeModal, onSave }: RBACModalProps) {
    const initialPermissions: PermissionsState = {};
    permissionsList.forEach((perm) => {
        const staffPermission = (staff?.permissions ?? []).find((p: Permission) => p.name === perm.name);
        initialPermissions[perm.name] = staffPermission?.value || false;
    });

    const [permissions, setPermissions] = useState<PermissionsState>(initialPermissions);

    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
        view: false,
        action: false,
    });

    const togglePermission = (name: string) => {
        setPermissions(prev => ({ ...prev, [name]: !prev[name] }));
    };

    const toggleSection = (type: string) => {
        setCollapsedSections(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const handleSave = () => {
        const payload = permissionsList.map(perm => ({
            name: perm.name,
            type: perm.type,
            value: permissions[perm.name] || false
        }));

        // send to parent or API
        if (onSave) onSave(staff.id, payload);
        closeModal();
    };

    // Group permissions by type
    const groupedPermissions = permissionsList.reduce((groups: Record<string, Permission[]>, perm) => {
        if (!groups[perm.type]) groups[perm.type] = [];
        groups[perm.type].push(perm);
        return groups;
    }, {});

    return (
        <div className="bg-white p-6 rounded-lg w-[500px] max-w-[90vw] animate-fadeIn max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
                Assign Permissions to {staff.firstName} {staff.lastName}
            </h2>

            {Object.entries(groupedPermissions).map(([type, perms]) => (
                <div key={type} className="mb-6">
                    {/* Section header with toggle icon */}
                    <div
                        className="flex items-center justify-between cursor-pointer mb-2"
                        onClick={() => toggleSection(type)}
                    >
                        <h3 className="text-sm font-semibold text-gray-700 capitalize">{type} permissions</h3>
                        {collapsedSections[type] ? <span>{FaChevronRight({})}</span> : <span>{FaChevronDown({})}</span>}
                    </div>

                    {!collapsedSections[type] && (
                        <div className="grid grid-cols-1 gap-3">
                            {perms.map((perm) => (
                                <div key={perm.name} className="flex justify-between items-center p-3 border rounded-lg hover:shadow-sm transition-shadow bg-gray-50">
                                    <div>
                                        <span className="capitalize font-medium">{perm.displayName}</span>
                                        <p className="text-xs text-gray-500">{perm.description}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={permissions[perm.name]}
                                            onChange={() => togglePermission(perm.name)}
                                            disabled={perm.required}
                                            className="sr-only"
                                        />
                                        <div className={`w-11 h-6 bg-gray-300 rounded-full peer ${permissions[perm.name] ? 'bg-green-500' : ''} transition-colors`}></div>
                                        <div
                                            className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${permissions[perm.name] ? 'translate-x-5' : ''}`}
                                        />
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            <div className="flex justify-end space-x-3 mt-6">
                <button className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300" onClick={closeModal}>
                    Cancel
                </button>
                <button className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700" onClick={handleSave}>
                    Save
                </button>
            </div>
        </div>
    );
}

export default RBACModal;